import { useContext, useMemo } from 'react'

import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import { convertOccupancies, getDatesAndGuests, getOfferImageId, getSavedDetailsForOffer } from 'tripPlanner/components/Bookmark/BookmarkButton/utils'
import { useEditableTrips } from 'tripPlanner/hooks/api/trip'
import { EmptyArray } from 'lib/array/arrayUtils'
import SavePrompt from './SavePrompt'
import useNewTripNameFromOffer from 'tripPlanner/hooks/useNewTripNameFromOffer'
import useShouldShowTripPlannerSaveEntryPoints from 'tripPlanner/hooks/useShouldShowTripPlannerSaveEntryPoints'
import type { HotelBookmarkPayload } from 'tripPlanner/api/bookmark/types'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { BasicTrip } from 'tripPlanner/types/common'

interface Props {
  offer: App.HotelOffer | App.BedbankOffer
}

function SavePromptHotel({ offer }: Props) {
  const shouldShowTripPlannerSaveEntryPoints =
    useShouldShowTripPlannerSaveEntryPoints()
  const offerPageState = useContext(OfferPageStateContext)
  const datesAndGuests = getDatesAndGuests(offerPageState, undefined)

  const { data, isSuccess: tripsFetched } = useEditableTrips({
    enabled: shouldShowTripPlannerSaveEntryPoints,
  })
  const trips = data ?? EmptyArray as Array<BasicTrip>

  const details = getSavedDetailsForOffer(trips, offer.id)
  const isSaved = details.length > 0

  const newTripName = useNewTripNameFromOffer(
    offer,
    shouldShowTripPlannerSaveEntryPoints,
  )

  const bookmarkPayload = useMemo<HotelBookmarkPayload>(() => ({
    type: offer.type,
    code: offer.id,
    startDate: datesAndGuests.checkInDate?.format(ISO_DATE_FORMAT),
    endDate: datesAndGuests.checkOutDate?.format(ISO_DATE_FORMAT),
    occupancies: convertOccupancies(datesAndGuests.occupancies),
  }), [datesAndGuests, offer])

  if (!shouldShowTripPlannerSaveEntryPoints || isSaved || !tripsFetched) {
    return null
  }

  return <SavePrompt
    offerId={offer.id}
    offerTypeLabel="hotel"
    bookmarkPayload={bookmarkPayload}
    newTripName={newTripName}
    offerImageId={getOfferImageId(offer)}
    trips={trips}
  />
}

export default SavePromptHotel
